import { ReactElement, ReactNode } from 'react';

import {
  Box,
  Stack,
  StackProps,
  Typography,
  TypographyProps,
  BoxProps,
} from '@mui/material';
import clsx from 'clsx';

import { tokens } from '../../theme/generated/tokens';
import { SxProps } from '../../types';
import { HexIconElementProps } from '../hex-icons';

const classNames = {
  root: 'Placeholder',
  iconWrapper: 'Placeholder-IconWrapper',
  body: 'Placeholder-Body',
  actions: 'Placeholder-Actions',
};

export type PlaceholderProps = {
  stackProps?: StackProps;
  icon?: ReactElement<HexIconElementProps>;
  iconWrapperProps?: BoxProps;
  title: ReactNode;
  titleProps?: TypographyProps;
  name?: ReactNode;
  nameProps?: TypographyProps;
  body?: ReactNode;
  bodyProps?: TypographyProps;
  actions?: ReactNode;
  actionsProps?: BoxProps;
  sx?: SxProps;
  className?: string;
  testId?: string;
};

export const Placeholder = ({
  stackProps,
  icon,
  iconWrapperProps,
  title,
  titleProps,
  name,
  nameProps,
  body,
  bodyProps,
  actions,
  actionsProps,
  sx,
  className,
  testId,
}: PlaceholderProps) => {
  return (
    <Stack
      alignItems="center"
      {...stackProps}
      className={clsx(classNames.root, className, stackProps?.className)}
      sx={sx}
      data-testid={testId}
    >
      {!!icon && (
        <Box
          mb={12}
          {...iconWrapperProps}
          className={clsx(classNames.iconWrapper, iconWrapperProps?.className)}
        >
          {icon}
        </Box>
      )}
      {!!name && (
        <Typography
          variant="h6"
          color={tokens.theme.fg.structural.secondary}
          mb={4}
          textAlign="center"
          data-testid="message-name"
          {...nameProps}
        >
          {name}
        </Typography>
      )}
      <Typography
        variant="h6"
        color={tokens.theme.fg.structural.primary}
        textAlign="center"
        data-testid="message-title"
        {...titleProps}
      >
        {title}
      </Typography>
      {!!body && (
        <Typography
          variant="P12R"
          color={tokens.theme.fg.structural.secondary}
          textAlign="center"
          mt={8}
          data-testid="message-body"
          component="div"
          {...bodyProps}
          className={clsx(classNames.body, bodyProps?.className)}
        >
          {body}
        </Typography>
      )}
      {!!actions && (
        <Box
          mt={32}
          {...actionsProps}
          className={clsx(classNames.actions, actionsProps?.className)}
        >
          {actions}
        </Box>
      )}
    </Stack>
  );
};

Placeholder.classNames = classNames;
